import { inject, Injectable } from "@angular/core";
import { PermissionsService } from "./permissions.service";
import { ApplicationConfigService } from "../application-config.service";
import { RoleConstants } from "../../../common/roles.constrants";

/**
 * NUC-RBAC v1→v2 (TASK-RBAC): granular `entity:action` capability gate for the
 * tasksense tenant UI. Every product/admin screen and EVERY action button now
 * resolves a real catalog capability via hasCapability() instead of the coarse
 * UPPERCASE_SNAKE authorities (USERS_MANAGEMENT, MANAGE_SCHEDULERS, …) or
 * ROLE_ADMIN.
 *
 * The authority list comes from /oauth2/me (ApplicationConfigService.getAuthorities)
 * and already carries the dependency-closure read caps (e.g. the schedule wizard's
 * task/capability/userdata pickers just work because the closure grants
 * task-definition:list / capability:list / userdata:list alongside job:access).
 *
 * The coarse roles survive ONLY as the universal escape hatch — ROLE_ADMIN
 * (super-admin) and ROLE_DEVOPS (ops console) — mirroring the bookwise-ui
 * CapabilityCheckerService and the nucleus group→authority grant model. All
 * write/mutation gates resolve the literal `entity:action` cap and never fall
 * back to a coarse authority.
 *
 * Method names are kept stable so the lib adapters + templates that call
 * hasAccessToXxx() don't need to change; only the bodies were rewired to v2.
 */
@Injectable({ providedIn: 'root' })
export class PermissionCheckerService {

  permissionsService : PermissionsService = inject(PermissionsService);
  private config : ApplicationConfigService = inject(ApplicationConfigService);

  /* ── Capability gate ────────────────────────────────────────────────
   * The single granular primitive: resolves an `entity:action` capability against the
   * user's expanded authority list, and NOTHING else.
   *
   * The coarse ROLE_ADMIN / ROLE_DEVOPS escape hatch was removed. It made this method
   * return true for EVERY capability a holder of either role was asked about, which is a
   * client-side authority the server does not recognise: every server gate is a literal
   * @PreAuthorize("hasAuthority('entity:action')"), and the CapabilityCheckerService this
   * was documented as mirroring does not exist anywhere in nucleus.
   *
   * So the UI was strictly more permissive than the API. Measured before removal against
   * the live databases: Administrators already hold 112 of the 112 capabilities this app
   * gates, so they lose nothing. DevOps held 40 (bookwise) / 24 (goldfish) of 112 — the
   * rest was UI they could open and the server would refuse. Hiding what does not work is
   * the fix; the alternative is a screen that 403s after you click it.
   *
   * Explicit role checks (hasRoleAdmin, hasRoleAuditor, …) are unaffected: those ask a
   * real question about a real role rather than laundering it into every capability. */
  hasCapability(capability : string) : boolean {
    const authorities : string[] = this.config.getAuthorities() ?? [];
    return authorities.includes(capability);
  }

  // ── Users (entity `user`, nucleus-user) ────────────────────────────────────
  // No user:access cap — user:list is the screen-reach gate.

  hasAccessToViewUsers() : boolean            { return this.hasCapability(RoleConstants.USER_LIST); }
  hasAccessToCreateUser() : boolean           { return this.hasCapability(RoleConstants.USER_CREATE); }
  hasAccessToUpdateUser() : boolean           { return this.hasCapability(RoleConstants.USER_UPDATE); }
  hasAccessToBlockUser() : boolean            { return this.hasCapability(RoleConstants.USER_BLOCK); }
  hasAccessToUnblockUser() : boolean          { return this.hasCapability(RoleConstants.USER_UNBLOCK); }
  hasAccessToDeleteUser() : boolean           { return this.hasCapability(RoleConstants.USER_DELETE); }
  hasAccessToResetPasswordForUser() : boolean { return this.hasCapability(RoleConstants.USER_RESET_PASSWORD); }

  // ── User data (entity `userdata`) ──────────────────────────────────────────
  // hasAccessToUserDataManagement = screen-reach + list. The granular create/
  // update/delete gates back the per-row action buttons; purge maps to delete.

  hasAccessToUserDataManagement() : boolean {
    return this.hasCapability(RoleConstants.USERDATA_ACCESS)
        || this.hasCapability(RoleConstants.USERDATA_LIST);
  }
  hasAccessToViewUserData() : boolean   { return this.hasCapability(RoleConstants.USERDATA_VIEW)   || this.hasCapability(RoleConstants.USERDATA_LIST); }
  hasAccessToCreateUserData() : boolean { return this.hasCapability(RoleConstants.USERDATA_CREATE); }
  hasAccessToUpdateUserData() : boolean { return this.hasCapability(RoleConstants.USERDATA_UPDATE); }
  hasAccessToDeleteUserData() : boolean { return this.hasCapability(RoleConstants.USERDATA_DELETE); }
  /** Purge (by-type / all) is a bulk delete. */
  hasAccessToPurgeUserData() : boolean  { return this.hasCapability(RoleConstants.USERDATA_DELETE); }

  // ── User data types (entity `userdata-type`) ───────────────────────────────

  hasAccessToUserDataTypeManagement() : boolean {
    return this.hasCapability(RoleConstants.USERDATA_TYPE_ACCESS)
        || this.hasCapability(RoleConstants.USERDATA_TYPE_LIST);
  }
  /** Mutate the type config (options/attributes/templates) — update-level cap. */
  hasAccessToManageUserDataTypes() : boolean { return this.hasCapability(RoleConstants.USERDATA_TYPE_UPDATE); }
  hasAccessToCreateUserDataType() : boolean  { return this.hasCapability(RoleConstants.USERDATA_TYPE_CREATE); }
  hasAccessToDeleteUserDataType() : boolean  { return this.hasCapability(RoleConstants.USERDATA_TYPE_DELETE); }

  // ── Capability registry (entity `capability`) ──────────────────────────────

  hasAccessToCapabilityManagement() : boolean {
    return this.hasCapability(RoleConstants.CAPABILITY_ACCESS)
        || this.hasCapability(RoleConstants.CAPABILITY_LIST);
  }
  /** Create a draft capability. */
  hasAccessToCreateCapability() : boolean { return this.hasCapability(RoleConstants.CAPABILITY_CREATE); }
  /** Edit fields / update a draft capability. */
  hasAccessToUpdateCapability() : boolean { return this.hasCapability(RoleConstants.CAPABILITY_UPDATE); }
  /** Delete a draft capability. */
  hasAccessToDeleteCapability() : boolean { return this.hasCapability(RoleConstants.CAPABILITY_DELETE); }
  /** Lifecycle control: promote / deactivate / decommission. */
  hasAccessToControlCapability() : boolean { return this.hasCapability(RoleConstants.CAPABILITY_CONTROL); }
  /** Coarse "can mutate" gate used by canManage() in the capability screens —
   *  any of update / control / create / delete grants the management toolbar. */
  hasAccessToManageCapabilities() : boolean {
    return this.hasCapability(RoleConstants.CAPABILITY_UPDATE)
        || this.hasCapability(RoleConstants.CAPABILITY_CONTROL)
        || this.hasCapability(RoleConstants.CAPABILITY_CREATE)
        || this.hasCapability(RoleConstants.CAPABILITY_DELETE);
  }

  // ── Task catalog (entity `task-definition`) ────────────────────────────────

  hasAccessToViewSechedulingTasks() : boolean {
    return this.hasCapability(RoleConstants.TASK_DEFINITION_ACCESS)
        || this.hasCapability(RoleConstants.TASK_DEFINITION_LIST);
  }

  // ── Schedules / jobs (entity `job`) + scheduler control ────────────────────

  hasAccessToViewSchedulingSchedules() : boolean {
    return this.hasCapability(RoleConstants.JOB_ACCESS)
        || this.hasCapability(RoleConstants.JOB_LIST)
        || this.hasCapability(RoleConstants.SCHEDULER_VIEW);
  }
  hasAccessToCreateSchedule() : boolean  { return this.hasCapability(RoleConstants.JOB_CREATE); }
  hasAccessToUpdateSchedule() : boolean  { return this.hasCapability(RoleConstants.JOB_UPDATE); }
  hasAccessToDeleteSchedule() : boolean  { return this.hasCapability(RoleConstants.JOB_DELETE); }
  hasAccessToControlSchedule() : boolean { return this.hasCapability(RoleConstants.JOB_CONTROL) || this.hasCapability(RoleConstants.SCHEDULER_CONTROL); }
  hasAccessToRunSchedule() : boolean     { return this.hasCapability(RoleConstants.JOB_RUN) || this.hasCapability(RoleConstants.SCHEDULER_RUN); }

  // ── Dashboard tiles (read-only views over job/runner/scheduler data) ───────
  // Backed by the same read caps as their underlying entities. Tiles are
  // view-only; no mutating actions live here.

  hasAccessToDeshboardQueue() : boolean          { return this.hasCapability(RoleConstants.JOB_LIST) || this.hasCapability(RoleConstants.SCHEDULER_VIEW); }
  hasAccessToDeshboardRunningTasks() : boolean   { return this.hasCapability(RoleConstants.JOB_LIST) || this.hasCapability(RoleConstants.SCHEDULER_VIEW); }
  hasAccessToDeshboardRunners() : boolean        { return this.hasCapability(RoleConstants.RUNNER_LIST); }
  hasAccessToDeshboardResourceStatus() : boolean { return this.hasCapability(RoleConstants.RUNNER_LIST) || this.hasCapability(RoleConstants.SCHEDULER_VIEW); }
  hasAccessToDeshboardStatistics() : boolean     { return this.hasCapability(RoleConstants.JOB_LIST) || this.hasCapability(RoleConstants.SCHEDULER_VIEW); }

  // ── Runner registry (entity `runner`) ──────────────────────────────────────

  hasAccessToViewRunnersAdmin() : boolean {
    return this.hasCapability(RoleConstants.RUNNER_ACCESS)
        || this.hasCapability(RoleConstants.RUNNER_LIST);
  }
  /** Runner-level control actions (cancel / drain / restart). */
  hasAccessToManageRunners() : boolean { return this.hasCapability(RoleConstants.RUNNER_CONTROL); }

  // ── Data sources (entity `datasource`) ─────────────────────────────────────

  hasAccessToDataSources() : boolean      { return this.hasCapability(RoleConstants.DATASOURCE_ACCESS) || this.hasCapability(RoleConstants.DATASOURCE_LIST); }
  hasAccessToViewDataSource() : boolean   { return this.hasCapability(RoleConstants.DATASOURCE_VIEW) || this.hasCapability(RoleConstants.DATASOURCE_LIST); }
  hasAccessToCreateDataSource() : boolean { return this.hasCapability(RoleConstants.DATASOURCE_CREATE); }
  hasAccessToUpdateDataSource() : boolean { return this.hasCapability(RoleConstants.DATASOURCE_UPDATE); }
  hasAccessToDeleteDataSource() : boolean { return this.hasCapability(RoleConstants.DATASOURCE_DELETE); }

  // ── Access requests (nucleus-user) ─────────────────────────────────────────

  hasAccessToAccessRequestManagement() : boolean { return this.hasCapability(RoleConstants.ACCESS_REQUEST_LIST); }
  hasAccessToApproveAccessRequest() : boolean     { return this.hasCapability(RoleConstants.ACCESS_REQUEST_APPROVE); }
  hasAccessToRejectAccessRequest() : boolean      { return this.hasCapability(RoleConstants.ACCESS_REQUEST_REJECT); }

  // ── Reports (nucleus-audit / nucleus-mail) ─────────────────────────────────

  hasAccessToAuditReport() : boolean { return this.hasCapability(RoleConstants.AUDIT_LIST) || this.hasCapability(RoleConstants.AUDIT_REPORT); }
  hasAccessToMailReport() : boolean  { return this.hasCapability(RoleConstants.MAIL_REPORT); }
  hasAccessToMailTemplates() : boolean { return this.hasCapability(RoleConstants.MAIL_TEMPLATE_LIST); }
  /** Per-row audit trail dialog. */
  hasAccessToAuditTrail() : boolean  { return this.hasCapability(RoleConstants.AUDIT_VIEW) || this.hasCapability(RoleConstants.AUDIT_LIST); }

  // The product reports screens are <app-coming-soon> stubs; these gates back
  // the report menu items only.
  hasAccessToTasksExecutionReport() : boolean { return this.hasCapability(RoleConstants.TASK_DEFINITION_LIST); }
  hasAccessToRunnersReport() : boolean        { return this.hasCapability(RoleConstants.RUNNER_LIST); }
  hasAccessToTasksReport() : boolean          { return this.hasCapability(RoleConstants.TASK_DEFINITION_LIST); }

  // ── IP Blocklist (nucleus gateway) ─────────────────────────────────────────

  hasAccessToIpBlocklist() : boolean { return this.hasCapability(RoleConstants.IP_BLACKLIST_LIST); }
  hasAccessToBlockIp() : boolean     { return this.hasCapability(RoleConstants.IP_BLACKLIST_BLOCK); }
  hasAccessToUnblockIp() : boolean   { return this.hasCapability(RoleConstants.IP_BLACKLIST_UNBLOCK); }

  // ── System Properties (nucleus-system-property) ────────────────────────────

  hasAccessToSystemProperty() : boolean       { return this.hasCapability(RoleConstants.SYSTEM_PROPERTY_LIST); }
  hasAccessToViewSystemProperty() : boolean   { return this.hasCapability(RoleConstants.SYSTEM_PROPERTY_VIEW) || this.hasCapability(RoleConstants.SYSTEM_PROPERTY_LIST); }
  hasAccessToUpdateSystemProperty() : boolean { return this.hasCapability(RoleConstants.SYSTEM_PROPERTY_UPDATE); }
  hasAccessToSystemPropertyReport() : boolean { return this.hasCapability(RoleConstants.SYSTEM_PROPERTY_REPORT); }

  // ── Reference Data (nucleus-reference-data) ────────────────────────────────

  hasAccessToReferenceData() : boolean       { return this.hasCapability(RoleConstants.REFERENCE_DATA_LIST) || this.hasCapability(RoleConstants.REFERENCE_DATA_VIEW); }
  hasAccessToViewReferenceData() : boolean   { return this.hasCapability(RoleConstants.REFERENCE_DATA_VIEW) || this.hasCapability(RoleConstants.REFERENCE_DATA_LIST); }
  hasAccessToCreateReferenceData() : boolean { return this.hasCapability(RoleConstants.REFERENCE_DATA_CREATE); }
  hasAccessToUpdateReferenceData() : boolean { return this.hasCapability(RoleConstants.REFERENCE_DATA_UPDATE); }
  hasAccessToDeleteReferenceData() : boolean { return this.hasCapability(RoleConstants.REFERENCE_DATA_DELETE); }
  hasAccessToReferenceDataReport() : boolean { return this.hasCapability(RoleConstants.REFERENCE_DATA_REPORT); }
  /** ADMIN-ONLY gate for the GLOBAL bucket browser (enumerate + create buckets
   *  across the whole tenant). Distinct from the per-bucket view/list caps. */
  hasAccessToManageReferenceData() : boolean { return this.hasCapability(RoleConstants.REFERENCE_DATA_MANAGE); }

  // ── Connections (nucleus-connectors) ───────────────────────────────────────

  hasAccessToConnections() : boolean { return this.hasCapability(RoleConstants.CONNECTIONS_ACCESS) || this.hasCapability(RoleConstants.CONNECTIONS_LIST); }

  // ── Org units (nucleus-user) ───────────────────────────────────────────────

  hasAccessToViewOrgUnits() : boolean   { return this.hasCapability(RoleConstants.ORG_UNIT_LIST); }
  hasAccessToCreateOrgUnit() : boolean  { return this.hasCapability(RoleConstants.ORG_UNIT_CREATE); }
  hasAccessToUpdateOrgUnit() : boolean  { return this.hasCapability(RoleConstants.ORG_UNIT_UPDATE); }
  hasAccessToDeleteOrgUnit() : boolean  { return this.hasCapability(RoleConstants.ORG_UNIT_DELETE); }
}
